/**
 * /manage/sites — web artifacts published by webgen (the web-artifacts
 * sibling service).
 *
 * Every active site, with access control per row: an access select
 * (public / link / space), "Share with people" (named people open it with
 * their own sign-in, and nothing else in Chaidojo), a share-link rotate for
 * link-gated sites (the old token dies; the fresh URL is surfaced once,
 * copied), Open, and Archive (takes the site offline and frees its slug). Everything proxies through
 * /admin/webgen — webgen is the system-of-record, CD stores nothing.
 *
 * A link-gated site's URL only works with its capability token, and the list
 * deliberately omits it — "Open" fetches the site detail (which carries the
 * tokened URL) and points the tab there. On a deployment without webgen the
 * list call answers 404 and the page renders a quiet "not enabled" note.
 */

import { ApiError } from "@chai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { Archive, ExternalLink, RotateCw, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useApi } from "@/api";
import { ArtifactPeoplePanel } from "@/components/manage/ArtifactPeoplePanel";
import { PageHeader } from "@/components/manage/PageHeader";
import { EmptyRow, Section } from "@/components/manage/Section";
import { StatStrip } from "@/components/manage/StatStrip";
import { StatusBadge } from "@/components/manage/StatusBadge";
import { useIsDemoManage } from "@/components/manage/org-context";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";

export const Route = createFileRoute("/manage/sites/")({
  component: SitesPage,
});

type SiteAccess = "public" | "link" | "space";

/** webgen's site "brief" — one list row (GET /admin/webgen/sites). */
interface WebgenSiteRow {
  id: string;
  slug: string;
  title: string;
  access: SiteAccess;
  status: string;
  version: number;
  url: string;
  /** Serving v2: the shareable `/s/{slug}` wrapper page (with `?k=` for a
   *  link site). Optional so a pre-v2 webgen still parses. */
  wrapper_url?: string;
  workspace_id: string;
  canvas_id: string | null;
  cd_artifact_id: string | null;
  updated_at: string;
}

/** Detail adds the publish history; for link sites `url` carries the token. */
interface WebgenSiteVersion {
  version: number;
  size_bytes: number;
  files: { path: string; size: number; content_type: string }[];
  warnings: string[];
  notes: string | null;
  author: string | null;
  created_at: string;
}

interface WebgenSiteDetail extends WebgenSiteRow {
  versions: WebgenSiteVersion[];
}

const ACCESS_TONE = {
  public: "ready",
  link: "info",
  space: "neutral",
} as const;

// Registered on Select.Root so the closed trigger renders the capitalized
// label (without this, base-ui falls back to the raw value string).
const ACCESS_ITEMS = [
  { value: "public", label: "Public" },
  { value: "link", label: "Link" },
  { value: "space", label: "Space" },
];

const SITES_KEY = ["admin-webgen-sites"] as const;

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false; // insecure context / permission denied — the toast still shows the URL
  }
}

/** Show a (possibly freshly minted) share URL once: copy it and toast it. */
async function surfaceLink(url: string, headline: string) {
  const copied = await copyText(url);
  toast.success(headline, {
    description: copied ? `Copied to clipboard: ${url}` : url,
    duration: 10000,
  });
}

function SitesPage() {
  const api = useApi();
  const qc = useQueryClient();
  // Demo tenants never see this surface — the nav hides it and the API refuses
  // demo actors; a typed-in URL gets a dead end, not a pitch.
  const isDemo = useIsDemoManage();
  // Row whose tokened link is being fetched for "Open".
  const [opening, setOpening] = useState<string | null>(null);
  // Row the "Share with people" dialog is for. Kept after the dialog closes,
  // so its content doesn't blank out during the closing animation.
  const [sharing, setSharing] = useState<WebgenSiteRow | null>(null);
  const [shareOpen, setShareOpen] = useState(false);

  const sites = useQuery({
    queryKey: SITES_KEY,
    enabled: !isDemo,
    queryFn: () => api.request<WebgenSiteRow[]>(`/admin/webgen/sites`),
    // 404 = webgen isn't configured on this deployment — a state, not a blip.
    retry: (count, err) =>
      !(err instanceof ApiError && err.status === 404) && count < 2,
  });
  const notEnabled =
    sites.isError && sites.error instanceof ApiError && sites.error.status === 404;

  const setAccess = useMutation({
    mutationFn: ({ id, access }: { id: string; access: SiteAccess }) =>
      api.request<WebgenSiteRow>(`/admin/webgen/sites/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ access }),
      }),
    // Optimistically flip the chip + select, then reconcile with the server.
    onMutate: async ({ id, access }) => {
      await qc.cancelQueries({ queryKey: SITES_KEY });
      const prev = qc.getQueryData<WebgenSiteRow[]>(SITES_KEY);
      qc.setQueryData<WebgenSiteRow[]>(SITES_KEY, (old) =>
        (old ?? []).map((s) => (s.id === id ? { ...s, access } : s)),
      );
      return { prev };
    },
    onError: (err: { message?: string }, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(SITES_KEY, ctx.prev);
      toast.error(err.message ?? "Access change failed");
    },
    onSuccess: (data, { access }) => {
      // Switching to link mints (or reuses) the token — hand the working URL
      // over right away instead of making the user dig for it.
      if (access === "link")
        void surfaceLink(data.wrapper_url ?? data.url, "Link access on");
      else toast.success(access === "public" ? "Site is now public" : "Access updated");
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: SITES_KEY }),
  });

  const rotate = useMutation({
    mutationFn: (id: string) =>
      api.request<WebgenSiteRow>(`/admin/webgen/sites/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ rotate_link: true }),
      }),
    onError: (err: { message?: string }) =>
      toast.error(err.message ?? "Rotate failed"),
    onSuccess: (data) =>
      void surfaceLink(
        data.wrapper_url ?? data.url,
        "Link rotated — the old link no longer works",
      ),
    onSettled: () => void qc.invalidateQueries({ queryKey: SITES_KEY }),
  });

  const archive = useMutation({
    mutationFn: (id: string) =>
      api.request<WebgenSiteRow>(`/admin/webgen/sites/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ archived: true }),
      }),
    // The server list only carries active sites, so drop the row immediately.
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: SITES_KEY });
      const prev = qc.getQueryData<WebgenSiteRow[]>(SITES_KEY);
      qc.setQueryData<WebgenSiteRow[]>(SITES_KEY, (old) =>
        (old ?? []).filter((s) => s.id !== id),
      );
      return { prev };
    },
    onError: (err: { message?: string }, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData(SITES_KEY, ctx.prev);
      toast.error(err.message ?? "Archive failed");
    },
    onSuccess: () => toast.success("Site archived"),
    onSettled: () => void qc.invalidateQueries({ queryKey: SITES_KEY }),
  });

  /** Open the site in a new tab — at its `/s/{slug}` wrapper page, which is
   *  what a person should ever see top-level (an artifact URL just redirects
   *  there). A link site needs the capability token, which only the DETAIL
   *  response carries, so open the tab first (the popup allowance rides the
   *  click), then point it there. */
  async function openSite(s: WebgenSiteRow) {
    if (s.access !== "link") {
      window.open(s.wrapper_url ?? s.url, "_blank", "noopener,noreferrer");
      return;
    }
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    setOpening(s.id);
    try {
      const detail = await api.request<WebgenSiteDetail>(
        `/admin/webgen/sites/${s.id}`,
      );
      const target = detail.wrapper_url ?? detail.url;
      if (tab) tab.location.href = target;
      else window.open(target, "_blank", "noopener,noreferrer");
    } catch (err) {
      tab?.close();
      toast.error(
        (err as { message?: string }).message ?? "Couldn't fetch the site link",
      );
    } finally {
      setOpening(null);
    }
  }

  const rows = sites.data ?? [];
  const byAccess = (a: SiteAccess) => rows.filter((s) => s.access === a).length;

  if (isDemo) {
    return (
      <div className="mx-auto max-w-[960px] px-10 py-10 text-sm text-muted-foreground">
        Not available in this demo.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[960px] px-10 py-10">
      <PageHeader
        eyebrow="Admin · Publishing"
        title="Web artifacts"
        description="Interactive pages and mini-apps the agent publishes from canvases. Set who can reach each one, rotate share links, and archive what's done."
      />

      {notEnabled ? (
        <Section title="Sites">
          <div className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-10 text-center text-sm text-muted-foreground">
            Web artifacts aren't enabled on this deployment — there's no webgen
            service configured. Once there is, sites the agent publishes show up
            here.
          </div>
        </Section>
      ) : (
        <>
          <StatStrip
            items={[
              { label: "Sites", value: rows.length, hint: "published, active" },
              { label: "Public", value: byAccess("public"), hint: "anyone with the URL" },
              { label: "Link", value: byAccess("link"), hint: "token-gated link" },
              { label: "Space", value: byAccess("space"), hint: "workspace members" },
            ]}
          />

          <Section
            title="All sites"
            description="Archiving takes a site offline and frees its address for reuse."
          >
            {sites.isLoading ? (
              <Skeleton className="h-40 w-full rounded-md" />
            ) : sites.isError ? (
              <div className="rounded-lg border border-dashed border-[var(--color-hairline)] bg-card px-4 py-10 text-center text-sm text-muted-foreground">
                Couldn't load sites — {(sites.error as { message?: string }).message ?? "webgen didn't answer"}.
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-[var(--color-hairline)] bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-surface-soft)]">
                    <tr className="text-left">
                      <th className="w-52 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        Slug
                      </th>
                      <th className="px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        Title
                      </th>
                      <th className="w-20 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        Access
                      </th>
                      <th className="w-14 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        Ver
                      </th>
                      <th className="w-28 px-4 py-2.5 font-mono text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                        Updated
                      </th>
                      <th className="w-60 px-4 py-2.5" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-hairline-soft)]">
                    {rows.length ? (
                      rows.map((s) => (
                        <tr
                          key={s.id}
                          className="group transition hover:bg-[var(--color-surface)]"
                        >
                          <td className="max-w-52 truncate px-4 py-3">
                            <code
                              className="rounded bg-[var(--color-surface-soft)] px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
                              title={s.slug}
                            >
                              {s.slug}
                            </code>
                          </td>
                          <td className="px-4 py-3 font-medium text-foreground">
                            {s.title}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge label={s.access} tone={ACCESS_TONE[s.access] ?? "neutral"} />
                          </td>
                          <td className="px-4 py-3 font-mono text-xs tabular-nums text-muted-foreground">
                            v{s.version}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(s.updated_at), {
                              addSuffix: true,
                            })}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-end gap-1.5">
                              <Select
                                items={ACCESS_ITEMS}
                                value={s.access}
                                onValueChange={(v) => {
                                  if (v && v !== s.access) {
                                    setAccess.mutate({
                                      id: s.id,
                                      access: v as SiteAccess,
                                    });
                                  }
                                }}
                              >
                                <SelectTrigger size="sm" aria-label="Access level">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ACCESS_ITEMS.map((it) => (
                                    <SelectItem key={it.value} value={it.value}>
                                      {it.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Share with people"
                                title="Share with people"
                                onClick={() => {
                                  setSharing(s);
                                  setShareOpen(true);
                                }}
                              >
                                <Users className="size-3.5 text-muted-foreground" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Open site"
                                title="Open site"
                                disabled={opening === s.id}
                                onClick={() => void openSite(s)}
                              >
                                {opening === s.id ? (
                                  <Spinner className="size-3.5" />
                                ) : (
                                  <ExternalLink className="size-3.5 text-muted-foreground" />
                                )}
                              </Button>
                              {s.access === "link" && (
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  aria-label="Rotate share link"
                                  title="Rotate share link — the current link stops working"
                                  disabled={rotate.isPending}
                                  onClick={() => {
                                    if (
                                      confirm(
                                        `Rotate the share link for "${s.title}"? Anyone using the current link loses access.`,
                                      )
                                    ) {
                                      rotate.mutate(s.id);
                                    }
                                  }}
                                >
                                  <RotateCw className="size-3.5 text-muted-foreground" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label="Archive site"
                                title="Archive site"
                                onClick={() => {
                                  if (
                                    confirm(
                                      `Archive "${s.title}"? The site goes offline and its address is freed for reuse.`,
                                    )
                                  ) {
                                    archive.mutate(s.id);
                                  }
                                }}
                              >
                                <Archive className="size-3.5 text-destructive" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <EmptyRow colSpan={6}>
                        No sites yet — when the agent publishes a web artifact
                        from a canvas, it lands here.
                      </EmptyRow>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Share with people</DialogTitle>
            <DialogDescription>
              {sharing?.title ?? ""}: people you add open it with their own
              sign-in, and can't open anything else in Chaidojo.
            </DialogDescription>
          </DialogHeader>
          {sharing ? <ArtifactPeoplePanel artifactRef={sharing.id} /> : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
